import React, {useState, useRef, useEffect, useCallback} from "react";
import {defineBlock, smartProps, useEditor, useInspectorControl, useSmartProp, ChildrenBlock, EditableImg} from "@smartfire/gutenberg-react";
import { PanelBody, PanelRow, TextControl, Button, ToggleControl  } from '@wordpress/components';
import {useScrollDirection} from '../shared/useScrollDirection';
import {useMedia} from 'use-media';
import DeleteIcon from '@material-ui/icons/Delete';
import { InnerBlocks } from '@wordpress/block-editor';
import style from "./block.module.scss";
import classnames from 'classnames';
import {ReactComponent as SearchSVG} from './search.svg'
import {ReactComponent as CloseSVG} from './close.svg'
import { useFocus, getSearchParam } from '../shared/utils';

function Inspector(){
    const [customMenu, setCustomMenu] = useSmartProp("customMenu");
    const [background, setBackground] = useSmartProp("background");
    const [theme, setTheme] = useSmartProp("theme");
    const [logoLink, setLogoLink] = useSmartProp("logoLink");
    const [showSearchBar, setShowSearchBar] = useSmartProp("showSearchBar");

    function deleteCategory(i){
        let copy=[...customMenu]
        copy.splice(i,1);
        setCustomMenu(copy);
    }

    function addCategory(){
        const copy=[...customMenu]
        copy.push({text : "", link: ""})
        setCustomMenu(copy);
    }

    function updateCategory(i, value, prop){
        let copy=[...customMenu];
        copy[i][prop]=value;
        setCustomMenu(copy);
    }

    return <><PanelBody title="Options" initialOpen={true}>
        <PanelRow>
            <ToggleControl
                label={theme ? 'Dark' : 'Light'}
                checked={theme}
                onChange={setTheme}
            />
        </PanelRow>
        <PanelRow>
            <ToggleControl
                label={background ? 'White' : 'Transaprent'}
                checked={background}
                onChange={setBackground}
            />
        </PanelRow>
        <PanelRow>
            <TextControl
                label="Logo Link"
                value={logoLink}
                onChange={ setLogoLink }
            />
        </PanelRow>
        <PanelRow>
            <ToggleControl
                label={"Show search bar"}
                checked={showSearchBar}
                onChange={setShowSearchBar}
            />
        </PanelRow>
    </PanelBody>
    <PanelBody className={style.panelBody} title="Setup menu" initialOpen={true}>
        <PanelRow className={style.panelRow}>
            <Button
                isPrimary={true}
                onClick={()=> addCategory()}>
                Add menu
            </Button>
        </PanelRow>
    </PanelBody>
    {customMenu && customMenu.length>0 && customMenu.map((m, i) => (
    <PanelBody key={i} className={style.panelBody} title={"Menu "+(i+1)} initialOpen={false}>
        <PanelRow className={style.panelRow}>
            <TextControl
                label="Menu name"
                value={m.text}
                className={style.textControl}
                onChange={(value) => updateCategory(i, value, "text")}
            />
        </PanelRow>
        <PanelRow className={style.panelRow}>
            <TextControl
                label="Link"
                value={m.link}
                className={style.textControl}
                onChange={(value) => updateCategory(i, value, "link")}
            />
        </PanelRow>
        <PanelRow className={style.panelRow}>
            <Button
                isPrimary={true}
                className={style.deleteButton}
                onClick={() => deleteCategory(i)}
            >
                <DeleteIcon/>Delete
            </Button>
        </PanelRow>
    </PanelBody>
    ))}
    </>
}

function SearchBar({opened, setOpened, setFocus}){
    return  <div className={style.searchBar}>
                <SearchSVG onClick={() => {
                    setOpened(!opened);
                    if(!opened) {
                        setFocus();
                    }
                }}/>
            </div>
}

function Block({theme, logoLink, customMenu, background, showSearchBar}){
    useInspectorControl(Inspector);
    const {isEditing} = useEditor();
    const [barWidth, setBarWidth] = useState(0);
    const [barLeft, setBarLeft] = useState(0);
    const [showMenu, setShowMenu] = useState(false);
    const [overflowBody, setOverflowBody] = useState('auto');
    const [currentLink, setCurrentLink] = useState(false);
    const [searchOpened, setSearchOpened] = useState(false);
    const headerEl = useRef(null);
    const buttonsEl = useRef(null);
    const isWide = useMedia({maxWidth: style.contentWidth});
    const scrollDir = useScrollDirection({initialDirection: 'up', thresholdPixels: isWide && buttonsEl && buttonsEl.current && buttonsEl.current.innerHTML !== '' ? parseInt(style.menuHeightMobile) : parseInt(style.menuHeight)});
    const Wrapper = isEditing ? 'div':'a';
    const buttonRef = useRef();

    useEffect(() => {
        if (showMenu) {
            const links = document.querySelectorAll('header .anchor')
            for (const link of links) {
                link.addEventListener('click', () => {
                    setShowMenu(false)
                    setOverflowBody('auto')
                })
            }
        }
    }, [showMenu])
    
    const toggleMenu = () => {
        if (overflowBody === 'auto') setOverflowBody('hidden')
        else setOverflowBody('auto')
        setShowMenu(show => !show)
    }

    const barHover = e => {
        const links = document.getElementById("menuHeader").querySelectorAll(".menu-item > a");
        links.forEach(el => el.classList.remove(style.active))
        if (e.target) e = e.target
        const left = e.getBoundingClientRect().left - document.getElementById("menuHeader").getBoundingClientRect().left;
        setBarWidth(e.offsetWidth);
        setBarLeft(left);
    };

    const barInit = useCallback(() => {
        if (currentLink) {
            const left = currentLink.getBoundingClientRect().left - document.getElementById("menuHeader").getBoundingClientRect().left;
            setBarWidth(currentLink.offsetWidth);
            setBarLeft(left);
            currentLink.classList.add(style.active)
        }
    }, [currentLink]);

    const barOut = useCallback(() => {
        barInit();
    }, [barInit])  

    function backgroundHeader () {
        if (!background) {
            if (window.pageYOffset - 20 > parseInt(isWide ? 150 : parseInt(style.menuHeight))) {
                headerEl.current.style.background = style.white;
            } else {
                headerEl.current.style.background = 'transparent';
            }
        }
    }

    useEffect(() => {
        function watchScroll() {
            window.addEventListener("scroll", backgroundHeader);
        }
        watchScroll();
        return () => {
            window.removeEventListener("scroll", backgroundHeader);
        };
    });

    useEffect(()=>{
       setTimeout(()=>{
            if(!isEditing){
                let contentDiv = buttonRef.current;
                let list = contentDiv.children[0].children;
                for(let i=0; i< list.length; i++){
                    let column = buttonsEl.current;
                    if(column){
                        column.appendChild(list[i].cloneNode(true))
                    }
                }   
                
            }
        }, 500);

    },[isEditing])
    useEffect(() => {
        if (scrollDir === 'up') headerEl.current.style.top = '0';
        else {
            headerEl.current.style.top = `-${isWide && buttonsEl && buttonsEl.current && buttonsEl.current.innerHTML !== '' ? style.menuHeightMobile : style.menuHeight}`;
            setSearchOpened(false);
        }
    }, [scrollDir, headerEl, isWide]);

    useEffect(() => {
        const links = document.getElementById("menuHeader").querySelectorAll(".menu-item > a");
        links.forEach(link => link.getAttribute('aria-current') === 'page' ? setCurrentLink(link): null);
        if (currentLink) barInit(currentLink);
        if(links) links.forEach(link => link.addEventListener("mouseover", barHover));
        if(links) links.forEach(link => link.addEventListener("mouseout", barOut));
        return () => {
            if(links) links.forEach(link => link.removeEventListener("mouseover", barHover));
            if(links) links.forEach(link => link.addEventListener("mouseout", barOut));
        }
    }, [barInit, barOut, currentLink]);

    const containerClasses = classnames({
        [style.container]: true,
        [style.admin]: isEditing,
        [style.background]: background,
        [style.lightTheme]: !theme,
        [style.noButtons]: buttonsEl && buttonsEl.current && buttonsEl.current.innerHTML === ''
    })

    const searchInputClasses = classnames({
        [style.searchInput]: true,
        [style.searchActive]: searchOpened
    })

    const search = getSearchParam('s');

    const [searchInputRef, setSearchInputFocus] = useFocus();

    document.body.style.overflow = overflowBody;

    if(!search) { // Prevent header change on reload if the client was on the search page
        document.cookie = 'search_ref='+(window.location.protocol + '//' + window.location.host + window.location.pathname)+';max-age=43200;path=/;SameSite=Strict';
    }

    return <div className={style.block}>
                <div ref={headerEl} className={containerClasses}>
                    <div className={showMenu ? style.contentExpanded : style.content}>
                        <div className={style.mobileHeader}>
                            <a href={isEditing ? () => false : logoLink} className={style.logo}>
                                <EditableImg smartProp="logo" style={{width: '170px'}} />
                            </a>
                            <div class={`${style.header} ${style.tablet}`}>
                                <div style={{ margin: 0 }} className={showMenu ? style.menuExpended : style.menu}>
                                    <ul>
                                    {customMenu && customMenu.map((menu, index) => (
                                        <React.Fragment key={index}>{menu.link && menu.text &&
                                            <li key={index} className="menu-item">
                                                <a href={menu.link} aria-current={window.location.href === menu.link && 'page'}>{menu.text}</a>
                                            </li>         
                                        }</React.Fragment>              
                                    ))}
                                    </ul>
                                    <div className={style.buttonsMobile} ref={buttonsEl}>
                                    </div>
                                </div>
                            </div>
                            <div className={style.mobileSearchBar}>
                                {showSearchBar &&
                                    <SearchBar opened={searchOpened} setOpened={setSearchOpened} setFocus={setSearchInputFocus} />
                                }
                            </div>
                            <div className={style.burger}>
                                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" className={style.burgerIcon} onClick={() => toggleMenu()} viewBox="0 0 40 30" >
                                    <rect x="0" y="0" className={showMenu && style.menuTopOpen} width="40" height="5" id="bar-top"></rect>
                                    <rect x="0" y="12.5" className={showMenu && style.menuMiddleOpen} width="40" height="5" id="bar-middle"></rect>
                                    <rect x="0" y="25" className={showMenu && style.menuBottomOpen} width="40" height="5" id="bar-bottom"></rect>
                                </svg>
                            </div>
                        </div>
                        <div class={style.header} id="menuHeader">
                            <div style={{ margin: 0 }} className={showMenu ? style.menuExpended : style.menu}>
                                <ul>
                                    {customMenu.map((menu, index) => (
                                        <React.Fragment key={index}>{menu.link && menu.text &&
                                            <li key={index} className="menu-item">
                                                <Wrapper href={menu.link} aria-current={window.location.pathname === menu.link + '/' && 'page'}>{menu.text}</Wrapper>
                                            </li>
                                        }</React.Fragment>
                                    ))}
                                </ul>
                            </div>
                            {showSearchBar &&
                                <SearchBar opened={searchOpened} setOpened={setSearchOpened} setFocus={setSearchInputFocus} />
                            }
                            <div className={style.headerBar} style={{width: barWidth, left: barLeft}} />
                            <div className={style.buttons} >
                            { isEditing ? 
                                <InnerBlocks
                                    allowedBlocks={["smartfire/button", "smartfire/air-liquide-button", "smartfire/separator"]}
                                    templateLock={false}
                                />:
                                <div ref={buttonRef}>
                                    <ChildrenBlock/>
                                </div>

                             }
                            </div>
                        </div>
                        {(showSearchBar && !isEditing) &&
                        <div className={searchInputClasses}>
                            <div>
                                <SearchSVG />
                                <form action={window.location.origin} method="GET">
                                    <input ref={searchInputRef} type="search" name="s" id="search_input" defaultValue={search} />
                                </form>
                                <CloseSVG className={style.closeBtn} onClick={() => {
                                    setSearchOpened(false);
                                }} />
                            </div>
                        </div>
                        }
                    </div>
                </div>
        </div>
}

export default defineBlock({
    Block, 
    smartProps: {
        customMenu: smartProps.any.withDefault([]),
        background: smartProps.bool.withDefault(true),
        theme: smartProps.bool.withDefault(true),
        logoLink: smartProps.bool.withDefault('/'),
        logo: smartProps.any.withDefault(),
        showSearchBar: smartProps.bool.withDefault(false)
    },
    setup: {
        title: "Header Page template",
        category: "two-third"
    }
})